import React, { useRef, useEffect, useMemo } from 'react';
import {
  ReportIcon,
  HomeIcon,
  AlertIcon,
  MessageHistoryIcon,
  MoneyIcon,
  EKGIcon,
  ExportIcon,
  KeyIcon,
  TruckIcon,
  BinIcon,
  UserIcon,
  OrgIcon,
} from '../../atoms/Icons';
import { useAtomValue } from 'jotai';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import useFeature from '../../utils/hooks/useFeature';
import useUser from '../../utils/hooks/useUser';
import { algorithmVersionAtom, featureFlagsAtom } from '../../utils/jotaiAtoms';
import ListOfButtons from './ListOfButtons';

import './SideBar.scss';
import { Privilege, useCan } from '@feedflo/access-control';

const BUTTONS = [
  {
    url: '/',
    icon: <HomeIcon />,
    text: 'My Barns',
    // Match either "/" or "/b/[some UUID]/[anything]"
    pathMatch: /^\/$|^\/b\/[0-9a-f]{8}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{12}\/.+$/,
    mobile: true,
  },
  {
    url: '/all-bins',
    icon: <BinIcon />,
    text: 'All Bins',
    pathMatch: /^\/all-bins$/,
    mobile: true,
  },
  {
    url: '/alerts?p=7&s=active&sortCol=nofeed_time&sortLevel=0&t=empty+pipe&t=inactive+auger&hideCharts=true&r=d906b529-865c-413f-824f-74005c7a0d63&r=01bc6865-8403-4961-8587-1685449e0920&r=09aa8f2a-7272-41c7-bb07-0203fde9e2cb&r=97d7b205-6fba-48a9-a8c2-49629698e92f&r=192c621a-16e4-4e8f-8657-4549c2605643&r=ea7bba00-23e1-40f7-83d6-dacebb5683ad&r=a758a08d-76ec-47f5-93c3-b1b1de495653&r=e91aeeba-1350-4ea4-8d04-81f7d4802a40&r=850b86a5-9e19-4a27-8b4a-3d3dca080e09&r=unidentified',
    icon: <AlertIcon />,
    text: 'Live Alert Hub',
    pathMatch:
      /alerts\?p=7&s=active&sortCol=nofeed_time&sortLevel=0&t=empty\+pipe&t=inactive\+auger&hideCharts=true/gimu,
    mobile: false,
  },
  {
    url: '/FeedDesk',
    icon: <TruckIcon />,
    text: 'Feed Desk',
    pathMatch: /^\/FeedDesk$/,
    mobile: false,
  },
  {
    url: '/alerts?p=90&n=severe&dataType=nofeed_time&t=empty+pipe&t=inactive+auger&r=d906b529-865c-413f-824f-74005c7a0d63&r=01bc6865-8403-4961-8587-1685449e0920&r=09aa8f2a-7272-41c7-bb07-0203fde9e2cb&r=97d7b205-6fba-48a9-a8c2-49629698e92f&r=192c621a-16e4-4e8f-8657-4549c2605643&r=ea7bba00-23e1-40f7-83d6-dacebb5683ad&r=a758a08d-76ec-47f5-93c3-b1b1de495653&r=e91aeeba-1350-4ea4-8d04-81f7d4802a40&r=850b86a5-9e19-4a27-8b4a-3d3dca080e09&r=unidentified',
    icon: <ReportIcon />,
    text: 'Management Report',
    pathMatch: /alerts\?p=90&n=severe&dataType=nofeed_time&t=empty\+pipe&t=inactive\+auger/gimu,
    mobile: false,
  },
  // {
  //   url: '/map',
  //   icon: <MapIcon />,
  //   text: 'Barn Map',
  //   pathMatch: /^\/map$/,
  //   mobile: true,
  // },
];

const STAFF_ONLY_BUTTONS = [
  {
    url: '/deviceStatus',
    icon: <HomeIcon />,
    text: 'IoT Dashboard',
    pathMatch: /^\/deviceStatus$/,
    mobile: true,
  },
  {
    url: '/firmware',
    icon: <MoneyIcon />,
    text: 'Firmware Control',
    pathMatch: /^\/firmware$/,
    mobile: true,
  },
  {
    url: '/simulations/barn',
    icon: <HomeIcon />,
    text: 'Barn Simulations',
    pathMatch: /^\/simulations\/barn$/,
    mobile: true,
  },
  {
    url: '/devices?name=Default+List&sort=nameAsc',
    icon: <KeyIcon />,
    text: 'Devices',
    pathMatch: /^\/devices$/,
    mobile: true,
  },
  {
    url: '/accuracyScorePage',
    icon: <EKGIcon />,
    text: 'Accuracy Score',
    pathMatch: /^\/accuracyScorePage$/,
    mobile: true,
  },
  {
    url: '/alerts?p=90&dataType=alerts&r=91f44d1c-af6d-491e-a027-fdd000b3676a&r=aedb6c4b-7715-4ef9-96fd-d122cca27fc0&r=01bc6865-8403-4961-8587-1685449e0920&r=ea7bba00-23e1-40f7-83d6-dacebb5683ad',
    icon: <AlertIcon />,
    text: 'Bug Monitor',
    pathMatch:
      /alerts\?p=90&dataType=alerts&r=91f44d1c-af6d-491e-a027-fdd000b3676a&r=aedb6c4b-7715-4ef9-96fd-d122cca27fc0&r=01bc6865-8403-4961-8587-1685449e0920&r=ea7bba00-23e1-40f7-83d6-dacebb5683ad/gimu,
    mobile: false,
  },
  {
    url: '/workshop',
    icon: '⚗️',
    text: "James' Workshop",
    pathMatch: /^\/workshop$/,
    mobile: true,
  },
  {
    url: '/faults/create',
    icon: <KeyIcon />,
    text: 'Fault Creator',
    pathMatch: /^\/faults/,
    mobile: false,
  },
  {
    url: '/users',
    icon: <UserIcon />,
    text: 'All Users',
    pathMatch: /^\/users$/,
    mobile: true,
  },
  {
    url: '/staff/algorithm-versions',
    icon: <KeyIcon />,
    text: 'Algorithm Versions',
    pathMatch: /^\/staff\/algorithm-versions$/,
    mobile: true,
  },
  {
    url: '/staff/largest-feedframes',
    icon: <HomeIcon />,
    text: 'Largest Feedframes',
    pathMatch: /^\/staff\/largest-feedframes$/,
    mobile: true,
  },
  {
    url: '/staff/device-monitor',
    icon: <HomeIcon />,
    text: 'Device Monitor',
    pathMatch: /^\/staff\/device-monitor$/,
    mobile: true,
  },
  {
    url: '/staff/crash-reports-monitor',
    icon: <AlertIcon />,
    text: 'Device Crash Monitor',
    pathMatch: /^\/staff\/crash-reports-monitor$/,
    mobile: true,
  }
];

function SideBarView({ open, onClickOutside, menuButtonRef, devInfoClassName = '' }) {
  const { user, loading: userLoading } = useUser();
  const algorithmVersion = useAtomValue(algorithmVersionAtom);
  const featureFlags = useAtomValue(featureFlagsAtom);
  const { active: isDeveloper } = useFeature('DEVELOPER_MODE');
  const { active: showStaffUI } = useFeature('SHOW_STAFF_ONLY_ELEMENTS');
  const { canAnywhere, loading: canLoading } = useCan();
  const sideBarRef = useRef(null);

  const activeFlagCount = featureFlags?.filter((x) => x.active)?.length || -1;
  const activeFlagNames = featureFlags?.filter((f) => f.active)?.map((f) => f.name) || [];
  const canViewOrgManagement =
    canAnywhere('org', Privilege.BARN_UPDATE) ||
    canAnywhere('org', Privilege.BARN_USERS_MANAGE) ||
    canAnywhere('org', Privilege.BARN_VIEW) ||
    canAnywhere('org', Privilege.ORG_BARN_CREATE) ||
    canAnywhere('org', Privilege.ORG_GENERAL_ADMINISTRATE) ||
    canAnywhere('org', Privilege.ORG_USER_VIEW) ||
    canAnywhere('org', Privilege.ORG_USERS_MANAGE) ||
    canAnywhere('org', Privilege.USER_ACCESS_MANAGE); // TODO: DISCUSS PRIVILEGES

  const canAccessAPI = canAnywhere('barn', Privilege.BARN_API);
  const canExportAnyBarn = canAnywhere('barn', Privilege.BARN_EXPORT);
  const showStaffButtons = user?.isStaff;

  const algorithmDisplayVersion = algorithmVersion?.substring(0, 7);
  const appDisplayVersion = import.meta.env.VITE_GIT_SHORT_HASH;

  const buttonList = useMemo(() => {
    let buttonsToDisplay = BUTTONS;

    // only bother checking privileges if privileges have loaded
    if (!userLoading && !canLoading) {
      if (canViewOrgManagement) {
        let orgLinkSuffix = user?.organizations?.length === 1 && !user?.isStaff ? `/${user?.organizations?.[0]}/` : '';

        buttonsToDisplay = buttonsToDisplay.concat([
          {
            url: `/orgs${orgLinkSuffix}`,
            icon: <OrgIcon />,
            text: 'My Organization',
            pathMatch: /^\/orgs.*/,
            mobile: true,
          },
        ]);
      }

      if (canAccessAPI) {
        buttonsToDisplay = buttonsToDisplay.concat([
          {
            url: '/apiAccess',
            icon: <KeyIcon />,
            text: 'Developers',
            pathMatch: /^\/apiAccess$/,
            mobile: true,
          },
        ]);
      }

      if (canExportAnyBarn) {
        buttonsToDisplay = buttonsToDisplay.concat([
          {
            url: '/exports',
            icon: <ExportIcon />,
            text: 'Data Export',
            pathMatch: /^\/exports$/,
            mobile: true,
          },
        ]);
      }


      buttonsToDisplay = buttonsToDisplay.concat([
        {
          url: '/message-history',
          icon: <MessageHistoryIcon />,
          text: 'Message History',
          pathMatch: /^\/message-history/,
          mobile: false,
        },
      ]);

    }

    return buttonsToDisplay;
  }, [user, userLoading, canLoading, canViewOrgManagement, canAccessAPI, canExportAnyBarn, canAnywhere]);

  const handleClickOutside = (event) => {
    if (
      (!sideBarRef?.current || !sideBarRef.current.contains(event.target)) &&
      (!menuButtonRef?.current || !menuButtonRef.current.contains(event.target))
    ) {
      onClickOutside();
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside]);

  return (
    <div ref={sideBarRef} className={`SideBarView ${open ? 'open' : ''}`}>
      <ListOfButtons
        onClick={onClickOutside}
        buttons={buttonList}
        staffOnlybuttons={showStaffButtons && !!showStaffUI ? STAFF_ONLY_BUTTONS : []}
      />
      <div className={`SideBarView-devInfo ${devInfoClassName}`}>
        <div className="SideBarView-frontendVersion">
          <label>App Version </label>
          <span>{appDisplayVersion}</span>
        </div>
        {algorithmVersion && (
          <div className="SideBarView-algVersion" title={algorithmVersion}>
            <label>Alg Version </label>
            <span>{algorithmDisplayVersion}</span>
          </div>
        )}
        {isDeveloper && <div className="SideBarView-devModeLabel">DEVELOPER MODE ACTIVE</div>}
        {activeFlagCount > 0 && (
          <Link to="/settings" onClick={onClickOutside}>
            <div className="SideBarView-flagsActiveLabel" title={activeFlagNames.join(', ')}>
              {activeFlagCount} Flag{activeFlagCount > 1 ? 's' : ''} Active
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}

SideBarView.propTypes = {
  open: PropTypes.bool,
  onClickOutside: PropTypes.func,
  devInfoClassName: PropTypes.string,
  menuButtonRef: PropTypes.object,
};

export default SideBarView;
